'use client';

import { useEffect, useRef, useState } from 'react';
import {
  ADJECTIVES,
  FANTASY_CODAS,
  FANTASY_MIDDLES,
  FANTASY_ONSETS,
  FANTASY_TITLES,
  FIRST_NAMES,
  LAST_NAMES,
  NICK_PREFIXES,
  NICK_SUFFIXES,
  NOUNS,
} from '@/lib/name-data';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

export type NameGeneratorVariant =
  | 'random-name-generator'
  | 'nickname-generator'
  | 'username-generator'
  | 'fantasy-name-generator';

function pick<T>(list: T[]): T {
  const buffer = new Uint32Array(1);
  crypto.getRandomValues(buffer);
  return list[buffer[0] % list.length];
}

function randomDigits(count: number): string {
  const buffer = new Uint32Array(1);
  crypto.getRandomValues(buffer);
  return String(buffer[0] % 10 ** count).padStart(count, '0');
}

function capitalize(word: string): string {
  return word[0].toUpperCase() + word.slice(1);
}

function generateOne(variant: NameGeneratorVariant, withNumbers: boolean): string {
  if (variant === 'random-name-generator') {
    return `${pick(FIRST_NAMES)} ${pick(LAST_NAMES)}`;
  }
  if (variant === 'nickname-generator') {
    const style = pick(['prefixSuffix', 'adjNoun', 'prefixNoun']);
    if (style === 'prefixSuffix') return `${pick(NICK_PREFIXES)}${pick(NICK_SUFFIXES)}`;
    if (style === 'adjNoun') return `${capitalize(pick(ADJECTIVES))}${capitalize(pick(NOUNS))}`;
    return `${pick(NICK_PREFIXES)}${capitalize(pick(NOUNS))}`;
  }
  if (variant === 'username-generator') {
    const base = pick([
      `${pick(ADJECTIVES)}_${pick(NOUNS)}`,
      `${pick(ADJECTIVES)}${capitalize(pick(NOUNS))}`,
      `${pick(NOUNS)}.${pick(ADJECTIVES)}`,
      `${pick(NICK_PREFIXES).toLowerCase()}_${pick(NOUNS)}`,
    ]);
    return withNumbers ? `${base}${randomDigits(2)}` : base;
  }
  const core = `${pick(FANTASY_ONSETS)}${pick(FANTASY_MIDDLES)}${pick(FANTASY_CODAS)}`;
  const style = pick(['plain', 'plain', 'titled']);
  return style === 'titled' ? `${core} ${pick(FANTASY_TITLES)}` : core;
}

const HEADINGS: Record<NameGeneratorVariant, { title: string; blurb: string; badge: string }> = {
  'random-name-generator': {
    title: 'Random names',
    blurb:
      'First and last name pairs drawn at random. Useful for test data, seating charts, story drafts, and placeholder records.',
    badge: 'Names',
  },
  'nickname-generator': {
    title: 'Nicknames',
    blurb:
      'Short, punchy nicknames built from three patterns so the list never feels like one template repeated.',
    badge: 'Nicknames',
  },
  'username-generator': {
    title: 'Usernames',
    blurb:
      'Handle-shaped names with underscores, dots, or camel case, and optional trailing digits for when the plain word is taken.',
    badge: 'Usernames',
  },
  'fantasy-name-generator': {
    title: 'Fantasy names',
    blurb:
      'Pronounceable invented names assembled from syllables, some with an epithet, for characters, campaigns, and worldbuilding.',
    badge: 'Fantasy',
  },
};

export default function BrowserNameGenerator({ variant }: { variant: NameGeneratorVariant }) {
  const usageReported = useRef(false);
  const [count, setCount] = useState(10);
  const [withNumbers, setWithNumbers] = useState(true);
  const [names, setNames] = useState<string[]>([]);
  const [copied, setCopied] = useState<string | null>(null);

  const heading = HEADINGS[variant];
  const isUsername = variant === 'username-generator';

  useEffect(() => {
    if (!usageReported.current && names.length) {
      usageReported.current = true;
      void consumeToolUsage(variant, '').catch(() => undefined);
    }
  }, [names, variant]);

  function generate() {
    const out = new Set<string>();
    // Draw a few extra rounds so duplicates do not shorten the visible list.
    for (let i = 0; i < count * 6 && out.size < count; i += 1) {
      out.add(generateOne(variant, withNumbers));
    }
    setNames([...out]);
    setCopied(null);
  }

  async function copyText(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(text);
      setTimeout(() => setCopied((current) => (current === text ? null : current)), 1400);
    } catch {
      setCopied(null);
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>{heading.title}</h2>
          <p>{heading.blurb}</p>
        </div>
        <span className="tool-live-badge">{heading.badge}</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader" style={{ alignItems: 'stretch', minHeight: 220 }}>
          {names.length ? (
            <ul
              style={{ display: 'grid', gap: '0.35rem', listStyle: 'none', margin: 0, padding: 0 }}
            >
              {names.map((name) => (
                <li key={name}>
                  <button
                    onClick={() => void copyText(name)}
                    style={{
                      background: copied === name ? 'var(--accent, #2a78d6)' : 'transparent',
                      border: '1px solid var(--border, #3a4152)',
                      borderRadius: 8,
                      color: 'inherit',
                      cursor: 'pointer',
                      padding: '0.45rem 0.7rem',
                      textAlign: 'left',
                      width: '100%',
                    }}
                    type="button"
                  >
                    {copied === name ? 'Copied!' : name}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="tool-live-panel-note">
              Press generate and the list appears here. Every name is built in your browser.
            </p>
          )}
        </div>

        <aside className="tool-live-panel">
          <label>
            How many
            <select onChange={(event) => setCount(Number(event.target.value))} value={count}>
              {[5, 10, 20, 50].map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>

          {isUsername ? (
            <label style={{ alignItems: 'center', display: 'flex', gap: '0.5rem' }}>
              <input
                checked={withNumbers}
                onChange={(event) => setWithNumbers(event.target.checked)}
                type="checkbox"
              />
              Add two trailing digits
            </label>
          ) : null}

          {names.length ? (
            <button
              className="button button-ghost"
              onClick={() => void copyText(names.join('\n'))}
              type="button"
            >
              Copy the whole list
            </button>
          ) : null}

          <p className="tool-live-panel-note">
            {isUsername
              ? 'This does not check whether a handle is free on any platform. Take a candidate to the service you want and try it there.'
              : 'Names are assembled from word banks in your browser, so nothing is stored and the same list never has to repeat.'}
          </p>
        </aside>
      </div>

      <div className="hero-actions tool-live-actions">
        <button className="button" onClick={generate} type="button">
          {names.length ? 'Generate again' : 'Generate names'}
        </button>
      </div>
    </article>
  );
}
